import { useState, useCallback, useRef, useLayoutEffect } from 'react'
import type { RefObject } from 'react'
import type { Attachment } from '../../../../../types/Document'
import { useAttachmentsViewDialog } from '../../../../../hooks/useDialog'
import usePdf, { PdfParams } from '../../../../../hooks/usePdf'
import { useDeviceType } from '../../../../../hooks/useDeviceType'

/**
 * フォールバック高さ（DOM計測前の初期値）
 * ダイアログが開いた直後、containerRefの高さが計測される前に使用される。
 * 70vhはビューポート高さの70%で、一般的なダイアログコンテンツに適した初期サイズ。
 */
const FALLBACK_HEIGHT = '70vh'

/**
 * コンテンツ領域の上下マージン（PC/SP共通）
 * 内訳: 上部余白 24px + 下部余白 24px = 48px
 * これによりナビゲーションボタンとコンテンツが重ならないようにする。
 */
const CONTENT_VERTICAL_MARGIN = 48

/**
 * SP版：コンテンツ領域の左右マージン
 * ダイアログのパディング分を考慮
 */
const CONTENT_HORIZONTAL_MARGIN_SP = 48

/** useAttachmentsViewDialogLogicの戻り値 */
export type AttachmentsViewDialogLogicResult = {
  /** ダイアログが開いているかどうか */
  isOpen: boolean
  /** ダイアログを閉じる関数 */
  close: () => void
  /** 現在表示中の添付ファイル（存在しない場合はundefined） */
  currentAttachment: Attachment | undefined
  /** 前のファイルへ移動 */
  goToPrevious: () => void
  /** 次のファイルへ移動 */
  goToNext: () => void
  /** 最初のファイルかどうか */
  isFirst: boolean
  /** 最後のファイルかどうか */
  isLast: boolean
  /** コンテナ要素へのref */
  containerRef: RefObject<HTMLDivElement>
  /** コンテンツの高さ */
  contentHeight: number | string
  /** コンテンツの幅（SP版用） */
  contentWidth: number | string
  /** PDFビューア用パラメータ */
  pdfParams: PdfParams
  /** PCデバイスかどうか */
  isPC: boolean
  /** モバイルデバイスかどうか */
  isMobile: boolean
}

/**
 * AttachmentsViewDialogの全てのロジックを管理するhook
 *
 * @param attachments - 添付ファイルリスト
 * @param initialId - 初期表示するファイルのID
 * @returns ダイアログの状態と操作関数
 */
export default function useAttachmentsViewDialogLogic({
  attachments,
  initialId,
}: {
  attachments: readonly Attachment[]
  initialId: string
}): AttachmentsViewDialogLogicResult {
  // デバイスタイプ
  const { pc: isPC, mobile: isMobile } = useDeviceType()

  // ダイアログの開閉状態
  const { state, close } = useAttachmentsViewDialog()
  const isOpen = state !== undefined

  // ナビゲーション状態
  const [currentIndex, setCurrentIndex] = useState(() => {
    const index = attachments.findIndex((item) => item.id === initialId)
    return index >= 0 ? index : 0
  })

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1))
  }, [])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(attachments.length - 1, prev + 1))
  }, [attachments.length])

  const currentAttachment = attachments[currentIndex]
  const isFirst = currentIndex === 0
  const isLast = currentIndex === attachments.length - 1

  // コンテナサイズの計測
  const containerRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState<number | string>(FALLBACK_HEIGHT)
  const [contentWidth, setContentWidth] = useState<number | string>('100%')

  // サイズを計測して更新する関数
  const measureAndSetDimensions = useCallback(() => {
    if (containerRef.current === null) return

    const measuredHeight = containerRef.current.clientHeight - CONTENT_VERTICAL_MARGIN

    if (measuredHeight > 0) {
      setContentHeight(measuredHeight)
    }

    // SP版では幅も計測（フル幅PDFのため）
    if (isMobile) {
      const measuredWidth = containerRef.current.clientWidth - CONTENT_HORIZONTAL_MARGIN_SP
      if (measuredWidth > 0) {
        setContentWidth(measuredWidth)
      }
    }
  }, [isMobile])

  // 初回マウント時とダイアログ状態変更時にサイズを計測
  useLayoutEffect(() => {
    measureAndSetDimensions()
  }, [state, measureAndSetDimensions])

  // ウィンドウリサイズ時にサイズを再計測
  useLayoutEffect(() => {
    if (!isOpen) return undefined

    const resizeObserver = new ResizeObserver(() => {
      measureAndSetDimensions()
    })

    if (containerRef.current !== null) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [isOpen, measureAndSetDimensions])

  // PDFパラメータ
  const pdfParams = usePdf({ filename: currentAttachment?.name ?? '' })

  return {
    isOpen,
    close,
    currentAttachment,
    goToPrevious,
    goToNext,
    isFirst,
    isLast,
    containerRef,
    contentHeight,
    contentWidth,
    pdfParams,
    isPC,
    isMobile,
  }
}
